// Druckansicht der Empfaenger OHNE E-Mail-Adresse: Name und Anschrift je Zeile, damit
// der Postweg (Serienbrief) vorbereitet werden kann. Overlay statt eigener Route — die
// Auswahl aus Schritt 1 bleibt so ohne Umweg ueber URL-State erhalten.
//
// Das @media-print-Stylesheet blendet ALLES ausser dem eigentlichen Druckbereich aus
// (auch die umgebende Sidebar/TopBar des Verwaltungsbereichs, die dieses Overlay nicht
// selbst kontrolliert) — deshalb ueber Sichtbarkeit statt Selektoren auf fremde
// Komponenten. window.print() erzeugt daraus das PDF, bewusst kein neuer PDF-Baukasten.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Printer } from '@phosphor-icons/react';
import { ImmoButton } from '../../../components/immobilien/ui';
import type { Kandidat } from '../../../lib/serienmail/typen';

interface DruckansichtProps {
  betreff: string;
  ohneAdresse: Kandidat[];
  onClose: () => void;
}

// Papierfarben: Die Liste ist ein Blatt zum Drucken – bewusst hell, auch im Dunkelmodus (README §9 PaperPreview).
const PAPIER = '#ffffff';
const TINTE = '#1b2433';
const TINTE2 = 'rgba(27,36,51,0.62)';

const Druckansicht: React.FC<DruckansichtProps> = ({ betreff, ohneAdresse, onClose }) => {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  return createPortal(
    <div className="serienmail-druck-huelle fixed inset-0 flex items-center justify-center" style={{ zIndex: 440, padding: 16, background: 'color-mix(in srgb, var(--bg) 55%, transparent)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .serienmail-druckbereich, .serienmail-druckbereich * { visibility: visible; }
          .serienmail-druckbereich { position: absolute; top: 0; left: 0; width: 100%; overflow: visible !important; }
          .serienmail-kein-druck { display: none !important; }
          .serienmail-druck-huelle, .serienmail-druck-blatt { position: static !important; overflow: visible !important; max-height: none !important; background: none !important; box-shadow: none !important; backdrop-filter: none !important; }
        }
      `}</style>

      <div role="dialog" aria-modal="true" aria-label="Druckliste — ohne E-Mail-Adresse" className="serienmail-druck-blatt immo-side-in flex flex-col w-full overflow-hidden"
        style={{ maxWidth: 720, maxHeight: 'calc(100dvh - 32px)', borderRadius: 26, background: PAPIER, color: TINTE, boxShadow: '0 40px 90px -30px rgba(0,0,0,0.55)' }}>
        <div className="serienmail-kein-druck flex items-center flex-wrap" style={{ gap: 10, padding: '16px 18px', borderBottom: '1px solid rgba(27,36,51,0.08)' }}>
          <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
            <h2 className="m-0" style={{ fontSize: 17, fontWeight: 800 }}>Druckliste — ohne E-Mail-Adresse</h2>
            <span style={{ fontSize: 12, color: TINTE2 }}>{ohneAdresse.length} Empfänger für den Postweg</span>
          </div>
          <ImmoButton art="akzent" groesse={44} icon={Printer} onClick={() => window.print()}>Drucken</ImmoButton>
          <button type="button" onClick={onClose} aria-label="Schließen" className="cursor-pointer"
            style={{ height: 44, padding: '0 16px', borderRadius: 22, border: 'none', background: 'rgba(27,36,51,0.07)', color: TINTE, fontSize: 13, fontWeight: 700 }}>
            Schließen
          </button>
        </div>

        <div className="serienmail-druckbereich overflow-y-auto" style={{ padding: '18px 22px 24px' }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: TINTE2 }}>Serienmail</p>
          <h1 style={{ margin: '2px 0 14px', fontSize: 20, fontWeight: 800 }}>{betreff || '(ohne Betreff)'}</h1>

          {ohneAdresse.length === 0 ? (
            <p style={{ fontSize: 13, color: TINTE2 }}>Alle Empfänger sind per E-Mail erreichbar — keine Druckliste nötig.</p>
          ) : (
            <div className="flex flex-col">
              {ohneAdresse.map((k) => (
                <div key={`${k.owner_type}-${k.owner_id}`} className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(27,36,51,0.08)', fontSize: 13, breakInside: 'avoid' }}>
                  <b style={{ overflowWrap: 'anywhere' }}>{k.name}</b>
                  <div className="flex flex-col" style={{ color: TINTE }}>
                    {/* Bei Mietern ist das die Objektanschrift plus Einheit — nur so landet
                        der Brief im richtigen Briefkasten. */}
                    {k.einheit_bezeichnung && <span>{k.einheit_bezeichnung}</span>}
                    {(k.strasse || k.hausnummer) && <span>{[k.strasse, k.hausnummer].filter(Boolean).join(' ')}</span>}
                    <span>{[k.plz, k.stadt].filter(Boolean).join(' ')}</span>
                    {k.objekt_bezeichnung && <span style={{ fontSize: 11, color: TINTE2, marginTop: 2 }}>{k.objekt_bezeichnung}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Druckansicht;
